import { Injectable } from '@angular/core';
import { Observable, combineLatest, EMPTY } from 'rxjs';
import { map, shareReplay, tap, switchMap } from 'rxjs/operators';
import { concatLatestFrom } from '@ngrx/operators';
import { RoiStore, MicroLayoutStore, AssetStore, AnalystStore } from '../../stores';
import { RoiLabelingEffects } from '../roi-labeling';
import { ProtocolStore } from '@telespot/protocols/ui';
import { Coordinates, Roi } from '@analysis/domain';
import { OverlayData } from '@shared/ui';

@Injectable()
export class MicroRoiDrawingEffects {
  private readonly MIN_ROI_SIDE_PX = 8;

  private readonly selectedLabels$ = combineLatest([
    this.protocolStore.options$,
    this.protocolStore.pipelines$,
    this.protocolStore.categories$,
    this.microLayout.selectedLabels$,
  ]).pipe(
    map(([options, pipelines, categories, selectedLabelIds]) => {
      if (!options || !categories) return [];
      if (!selectedLabelIds || selectedLabelIds.size === 0) return [];
      const labels = Object.values(options)
        .filter((option) => selectedLabelIds.has(option.id))
        .map((option) => {
          const category = Object.values(categories).find((cat) => cat.options.includes(option.id));
          const pipeline = pipelines.find((pl) => pl.categories.includes(category?.id));
          return {
            ...option,
            category: category?.name,
            stepId: category?.stepId,
            pipelineId: pipeline?.id,
          };
        });
      return labels;
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  private readonly activeRoiFindings$ = combineLatest([
    this.assetStore.selectedAsset$,
    this.analystStore.selectedAnalyst$,
    this.roiStore.roiFindings$,
  ]).pipe(
    map(([selectedAsset, selectedAnalyst, roiFindings]) => {
      if (!selectedAsset || !selectedAnalyst || !roiFindings) return [];
      return Array.from(roiFindings.values()).filter(
        (roiFinding) => roiFinding.creatorId === selectedAnalyst.id && roiFinding.assetId === selectedAsset.id
      );
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  constructor(
    private readonly assetStore: AssetStore,
    private readonly analystStore: AnalystStore,
    private readonly roiStore: RoiStore,
    private readonly roiLabelingEffects: RoiLabelingEffects,
    private readonly microLayout: MicroLayoutStore,
    private readonly protocolStore: ProtocolStore
  ) {}

  // --- Effects ---

  public readonly addRois = this.roiStore.effect((action$: Observable<{ overlay: any; sampleId: string }>) =>
    action$.pipe(
      concatLatestFrom(() => [
        this.assetStore.selectedAsset$,
        this.analystStore.selectedAnalyst$,
        this.selectedLabels$,
        this.activeRoiFindings$,
        this.protocolStore.categories$,
      ]),
      tap(([{ overlay, sampleId }, selectedAsset, selectedAnalyst, selectedLabels, roiFindings, categories]) => {
        const assetId = selectedAsset?.id;
        const creatorId = selectedAnalyst?.id;
        const creatorEntity = selectedAnalyst?.type;

        if (this.shouldDiscardRoi(overlay)) return EMPTY;

        if (!assetId || !sampleId || !creatorId || selectedLabels.length === 0) return;

        const coordinates = this.normalizeCoordinates(overlay, selectedAsset?.data?.width, selectedAsset?.data?.height);

        const payload = {
          coordinates,
          assetId,
          sampleId,
          roiFindings,
          selectedLabels,
          categories: categories || {},
          creatorId,
          creatorEntity,
          additionalLabelsIncluded: true,
        };

        this.roiLabelingEffects.createNewRoi(payload);
        this.updateBoxDimensions(overlay, selectedLabels);
      }),
      switchMap(() => EMPTY)
    )
  );

  public readonly updateRoiPosition = this.roiStore.effect((action$: Observable<{ overlay: OverlayData }>) =>
    action$.pipe(
      concatLatestFrom(() => [this.assetStore.selectedAsset$, this.selectedLabels$]),
      tap(([{ overlay }, selectedAsset, selectedLabels]) => {
        if (this.shouldDiscardRoi(overlay)) return EMPTY;

        const assetWidth = selectedAsset?.data?.width || 1;
        const assetHeight = selectedAsset?.data?.height || 1;
        const normalizedCoordinates = this.normalizeCoordinates(overlay, assetWidth, assetHeight);
        const roiUpdate: Partial<Roi> = { id: overlay.id, coordinates: normalizedCoordinates };
        this.roiLabelingEffects.updateRoiPosition({ roi: roiUpdate as Roi });
        this.updateBoxDimensions(overlay, selectedLabels);
      }),
      switchMap(() => EMPTY)
    )
  );

  // --- Private helpers ---

  private normalizeCoordinates(
    overlay: { x?: number; y?: number; w?: number; h?: number },
    assetWidth: number,
    assetHeight: number
  ): Coordinates {
    const width = assetWidth || 1;
    const height = assetHeight || 1;
    return {
      x0: (overlay.x || 0) / width,
      y0: (overlay.y || 0) / height,
      x1: ((overlay.x || 0) + (overlay.w || 0)) / width,
      y1: ((overlay.y || 0) + (overlay.h || 0)) / height,
    };
  }

  private shouldDiscardRoi(overlay: OverlayData): boolean {
    const width = overlay.w ?? 0;
    const height = overlay.h ?? 0;

    if (width === 0 && height === 0) return false;
    if (width <= 0 || height <= 0) return true;

    return Math.min(width, height) < this.MIN_ROI_SIDE_PX;
  }

  private updateBoxDimensions(overlay: { w?: number; h?: number }, selectedLabels: any[]): void {
    if (overlay.w && overlay.h && selectedLabels.length > 0) {
      const dimensions = { defaultWidth: overlay.w, defaultHeight: overlay.h };
      if (selectedLabels.length === 1) {
        this.microLayout.updateBoxDimensionsForLabel({
          labelId: selectedLabels[0].id,
          dimensions,
        });
      } else {
        const comboKey = selectedLabels
          .map((l) => l.id)
          .slice()
          .sort()
          .join('|');
        this.microLayout.setMultiSelectionBoxDimensionsForCombo({ comboKey, dimensions });
      }
    }
  }
}
